import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    FormSection,
    MasterDataForm,
} from '../components/master-data/MasterDataForm';
import {
    FilterConfig,
    MasterDataTable,
    TableColumn,
} from '../components/master-data/MasterDataTable';

import { Badge } from '@/components/ui/badge';
import { useState } from 'react';
import { z } from 'zod';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';

interface Hub {
  _id: string;
  name: string;
  code: string;
  type?: 'Hub' | 'Franchise' | 'Branch' | 'District Hub' | 'Master District Hub' | 'Routing Hub' | 'State Capital Hub' | 'Zonal Hub' | 'Airport' | 'City';
  contactNo?: string;
  email?: string;
  address?: string;
  airportName?: string;
  airportCode?: string;
  country?: string;
  stateCode?: string;
  company: { _id: string; name: string } | string;
  hubTrackingName?: string;
  status: 'Active' | 'Inactive';
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
  updatedBy?: string;
}

const hubTypes = [
  { value: 'Hub', label: 'Hub' },
  { value: 'Franchise', label: 'Franchise' },
  { value: 'Branch', label: 'Branch' },
  { value: 'District Hub', label: 'District Hub' },
  { value: 'Master District Hub', label: 'Master District Hub' },
  { value: 'Routing Hub', label: 'Routing Hub' },
  { value: 'State Capital Hub', label: 'State Capital Hub' },
  { value: 'Zonal Hub', label: 'Zonal Hub' },
  { value: 'Airport', label: 'Airport' },
  { value: 'City', label: 'City' },
];

const statusOptions = [
  { value: 'Active', label: 'Active' },
  { value: 'Inactive', label: 'Inactive' },
];

const HubManagement = () => {
  const queryClient = useQueryClient();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingHub, setEditingHub] = useState<Hub | null>(null);
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const { axios } = useAuth();
  const { toast } = useToast();

  // State for server-side filtering and pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentFilters, setCurrentFilters] = useState<Record<string, string>>(
    {}
  );
  const [sortConfig, setSortConfig] = useState<{
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }>({});

  // Fetch hubs with server-side filtering and pagination
  const { data: hubsResponse, isLoading } = useQuery({
    queryKey: [
      'hubs',
      currentPage,
      pageSize,
      searchTerm,
      currentFilters,
      sortConfig,
    ],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('page', currentPage.toString());
      params.append('limit', pageSize.toString());

      if (searchTerm) {
        params.append('search', searchTerm);
      }

      // Add filters
      Object.entries(currentFilters).forEach(([key, value]) => {
        if (value && value !== 'all') {
          params.append(key, value);
        }
      });

      // Add sorting
      if (sortConfig.sortBy) {
        params.append('sortBy', sortConfig.sortBy);
        params.append('sortOrder', sortConfig.sortOrder || 'asc');
      }

      const response = await axios.get(
        `/api/master-data/hub?${params.toString()}`
      );
      const result = response.data.data || {};
      console.log('Hub API response:', result);

      return {
        data: result.data || [],
        pagination: {
          total: result.total || 0,
          page: result.page || 1,
          limit: result.limit || 10,
          totalPages: result.totalPages || 1,
        },
      };
    },
  });

  const hubs = hubsResponse?.data || [];
  const pagination = hubsResponse?.pagination;

  // Fetch companies for dropdown
  const { data: companies = [] } = useQuery({
    queryKey: ['companies-dropdown'],
    queryFn: async () => {
      const response = await axios.get('/api/master-data/company');
      console.log('HubManagement - Companies API Response:', response.data);
      const companiesData =
        response.data.data?.data?.map((company: any) => ({
          value: company._id,
          label: company.name || 'Unknown Company',
        })) || [];
      console.log('HubManagement - Companies for dropdown:', companiesData);
      return companiesData;
    },
  });

  // Fetch countries for dropdown
  const { data: countries = [] } = useQuery({
    queryKey: ['countries-dropdown'],
    queryFn: async () => {
      const response = await axios.get('/api/master-data/country');
      console.log('HubManagement - Countries API Response:', response.data);
      const countriesData =
        response.data.data?.data?.map((country: any) => ({
          value: country.name || 'Unknown Country', // Use country name as value
          label: `${country.name || 'Unknown Country'} (${country.code || 'N/A'})`,
        })) || [];
      console.log('HubManagement - Countries for dropdown:', countriesData);
      return countriesData;
    },
  });

  // Create hub mutation
  const createHubMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await axios.post('/api/master-data/hub', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hubs'] });
      queryClient.invalidateQueries({ queryKey: ['master-data-count', 'hub'] });
      toast({
        title: 'Hub created',
        description: 'Hub has been created successfully.',
      });
      setIsFormOpen(false);
      setEditingHub(null);
      setServerErrors({});
    },
    onError: (error: any) => {
      console.log('Hub creation error:', error.response?.data);
      // Extract validation errors if available
      const responseData = error.response?.data;
      const validationErrors: Record<string, string> = {};

      if (responseData?.details && Array.isArray(responseData.details)) {
        // Handle array format validation errors
        responseData.details.forEach((detail: any) => {
          if (detail.field && detail.message) {
            validationErrors[detail.field] = detail.message;
          }
        });
      } else if (responseData?.errors) {
        // Handle object format validation errors
        Object.keys(responseData.errors).forEach(field => {
          validationErrors[field] = responseData.errors[field];
        });
      }

      if (Object.keys(validationErrors).length > 0) {
        setServerErrors(validationErrors);
        toast({
          title: 'Validation Error',
          description: 'Please check the form for validation errors.',
          variant: 'destructive',
        });
      } else {
        setServerErrors({});
        toast({
          title: 'Error creating hub',
          description:
            responseData?.error?.message ||
            responseData?.message ||
            'Failed to create hub',
          variant: 'destructive',
        });
      }
    },
  });

  // Update hub mutation
  const updateHubMutation = useMutation({
    mutationFn: async (data: any) => {
      const { _id, ...updateData } = data;
      const response = await axios.put(
        `/api/master-data/hub/${_id}`,
        updateData
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hubs'] });
      queryClient.invalidateQueries({ queryKey: ['master-data-count', 'hub'] });
      toast({
        title: 'Hub updated',
        description: 'Hub has been updated successfully.',
      });
      setIsFormOpen(false);
      setEditingHub(null);
      setServerErrors({});
    },
    onError: (error: any) => {
      console.log('Hub update error:', error.response?.data);
      // Extract validation errors if available
      const responseData = error.response?.data;
      const validationErrors: Record<string, string> = {};

      if (responseData?.details && Array.isArray(responseData.details)) {
        // Handle array format validation errors
        responseData.details.forEach((detail: any) => {
          if (detail.field && detail.message) {
            validationErrors[detail.field] = detail.message;
          }
        });
      } else if (responseData?.errors) {
        // Handle object format validation errors
        Object.keys(responseData.errors).forEach(field => {
          validationErrors[field] = responseData.errors[field];
        });
      }

      if (Object.keys(validationErrors).length > 0) {
        setServerErrors(validationErrors);
        toast({
          title: 'Validation Error',
          description: 'Please check the form for validation errors.',
          variant: 'destructive',
        });
      } else {
        setServerErrors({});
        toast({
          title: 'Error updating hub',
          description:
            responseData?.error?.message ||
            responseData?.message ||
            'Failed to update hub',
          variant: 'destructive',
        });
      }
    },
  });

  // Delete hub mutation
  const deleteHubMutation = useMutation({
    mutationFn: async (hubId: string) => {
      await axios.delete(`/api/master-data/hub/${hubId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hubs'] });
      queryClient.invalidateQueries({ queryKey: ['master-data-count', 'hub'] });
      toast({
        title: 'Hub deleted',
        description: 'Hub has been deleted successfully.',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error deleting hub',
        description:
          error.response?.data?.error?.message ||
          error.response?.data?.error ||
          'Failed to delete hub',
        variant: 'destructive',
      });
    },
  });

  const columns: TableColumn<Hub>[] = [
    {
      key: 'name',
      label: 'Hub Details',
      sortable: true,
      render: (value, hub) => (
        <div className='flex flex-col'>
          <div className='font-medium text-gray-900'>{value}</div>
          <div className='text-sm text-gray-500 font-mono bg-gray-100 px-2 py-1 rounded mt-1 inline-block w-fit'>
            {hub.code}
          </div>
          <div className='md:hidden mt-2 space-y-1'>
            <div className='flex flex-wrap gap-2'>
              <Badge
                variant={hub.status === 'Active' ? 'default' : 'secondary'}
              >
                {hub.status}
              </Badge>
              <Badge variant='outline'>{hub.type}</Badge>
            </div>
            {hub.contactNo && (
              <div className='text-sm text-gray-600'>📞 {hub.contactNo}</div>
            )}
            {hub.email && (
              <div className='text-sm text-gray-600'>✉️ {hub.email}</div>
            )}
            {hub.address && (
              <div className='text-sm text-gray-600'>📍 {hub.address}</div>
            )}
            {hub.country && (
              <div className='text-sm text-gray-600'>🌍 {hub.country}</div>
            )}
            {hub.company && (
              <div className='text-sm text-gray-600'>
                🏢{' '}
                {typeof hub.company === 'object' && hub.company !== null
                  ? hub.company.name || 'Unknown Company'
                  : hub.company || 'No Company'}
              </div>
            )}
          </div>
        </div>
      ),
    },
    {
      key: 'type',
      label: 'Type',
      sortable: true,
      render: value => (
        <Badge variant='outline' className='whitespace-nowrap'>
          {value}
        </Badge>
      ),
      hideOnMobile: true,
    },
    {
      key: 'company',
      label: 'Company',
      sortable: true,
      render: value => (
        <div className='max-w-[150px] truncate'>
          {typeof value === 'object' && value !== null
            ? value.name || 'Unknown Company'
            : value || 'No Company'}
        </div>
      ),
      hideOnTablet: true,
    },
    {
      key: 'contactNo',
      label: 'Contact',
      render: value => value || '-',
      hideOnTablet: true,
    },
    {
      key: 'email',
      label: 'Email',
      render: value => value || '-',
      hideOnTablet: true,
    },
    {
      key: 'country',
      label: 'Country',
      render: (value, hub) => (
        <div className='max-w-[150px]'>
          {value && <div className='text-sm truncate'>{value}</div>}
          {hub.stateCode && (
            <div className='text-xs text-gray-500 truncate'>
              State: {hub.stateCode}
            </div>
          )}
        </div>
      ),
      hideOnTablet: true,
    },
    {
      key: 'address',
      label: 'Location',
      render: (value, hub) => (
        <div className='max-w-[200px]'>
          {value && <div className='text-sm truncate'>{value}</div>}
          {hub.airportCode && (
            <div className='text-xs text-blue-600 font-mono'>
              Airport: {hub.airportCode}
            </div>
          )}
        </div>
      ),
      hideOnTablet: true,
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: value => (
        <Badge variant={value === 'Active' ? 'default' : 'secondary'}>
          {value}
        </Badge>
      ),
      hideOnMobile: true,
    },
    {
      key: 'createdAt',
      label: 'Created',
      sortable: true,
      render: value => new Date(value).toLocaleDateString(),
      hideOnTablet: true,
    },
  ];

  const filters: FilterConfig[] = [
    {
      key: 'type',
      label: 'Hub Type',
      type: 'select',
      options: [{ value: 'all', label: 'All Types' }, ...hubTypes],
      placeholder: 'Filter by type',
    },
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      options: [{ value: 'all', label: 'All Status' }, ...statusOptions],
      placeholder: 'Filter by status',
    },
    {
      key: 'country',
      label: 'Country',
      type: 'text',
      placeholder: 'Filter by country',
    },
    {
      key: 'stateCode',
      label: 'State Code',
      type: 'text',
      placeholder: 'Filter by state code',
    },
  ];

  const formSections: FormSection[] = [
    {
      title: 'Hub Information',
      description:
        'Configure hub details and basic information. Hub code will be auto-generated if not provided.',
      fields: [
        {
          name: 'name',
          label: 'Hub Name',
          type: 'text',
          required: true,
          placeholder: 'Enter unique hub name',
          validation: z
            .string()
            .min(2, 'Hub name must be at least 2 characters')
            .max(200, 'Hub name cannot exceed 200 characters')
            .regex(
              /^[a-zA-Z0-9\s\-_&.,()]+$/,
              'Hub name contains invalid characters'
            ),
        },
        {
          name: 'code',
          label: 'Hub Code',
          type: 'text',
          required: false,
          placeholder: 'Auto-generated if left empty',
          description: 'Unique hub identifier (auto-generated if not provided)',
          validation: z
            .string()
            .optional()
            .refine(
              val => !val || /^[A-Z0-9_-]+$/.test(val),
              'Code can only contain uppercase letters, numbers, underscores and hyphens'
            ),
        },
        {
          name: 'type',
          label: 'Hub Type',
          type: 'select',
          required: false,
          options: hubTypes,
          placeholder: 'Select hub type',
        },
        {
          name: 'company',
          label: 'Company',
          type: 'select',
          required: true,
          options: companies,
          placeholder: 'Select company',
          description: 'Company under which this hub operates',
        },
        {
          name: 'status',
          label: 'Status',
          type: 'radio',
          required: true,
          options: statusOptions,
        },
      ],
    },
    {
      title: 'Contact Information',
      description: 'Hub contact details and communication information',
      fields: [
        {
          name: 'contactNo',
          label: 'Contact Number',
          type: 'tel',
          required: false,
          placeholder: 'Enter contact number',
          validation: z
            .string()
            .optional()
            .refine(
              val => !val || /^[+]?[\d\s\-()]{10,15}$/.test(val),
              'Please enter a valid contact number'
            ),
        },
        {
          name: 'email',
          label: 'Email Address',
          type: 'email',
          required: false,
          placeholder: 'Enter email address',
          validation: z
            .string()
            .optional()
            .refine(
              val =>
                !val || /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(val),
              'Please enter a valid email address'
            ),
        },
      ],
    },
    {
      title: 'Location Details',
      description: 'Hub address and geographical information',
      fields: [
        {
          name: 'address',
          label: 'Address',
          type: 'textarea',
          required: false,
          placeholder: 'Enter full physical address',
          rows: 3,
          validation: z
            .string()
            .optional()
            .refine(
              val => !val || val.length <= 500,
              'Address cannot exceed 500 characters'
            ),
        },
        {
          name: 'country',
          label: 'Country',
          type: 'select',
          required: false,
          options: countries,
          placeholder: 'Select country',
          validation: z
            .string()
            .optional()
            .refine(
              val => !val || val.length <= 100,
              'Country name cannot exceed 100 characters'
            ),
        },
        {
          name: 'stateCode',
          label: 'State Code',
          type: 'text',
          required: false,
          placeholder: 'Enter state or province code',
          validation: z
            .string()
            .optional()
            .refine(
              val => !val || /^[A-Z0-9]{1,10}$/.test(val),
              'State code should be uppercase alphanumeric, max 10 characters'
            ),
        },
      ],
    },
    {
      title: 'Airport Information',
      description:
        'Airport-specific details (applicable for Airport type hubs)',
      fields: [
        {
          name: 'airportName',
          label: 'Airport Name',
          type: 'textarea',
          required: false,
          placeholder: 'Enter associated airport name',
          rows: 2,
          validation: z
            .string()
            .optional()
            .refine(
              val => !val || val.length <= 200,
              'Airport name cannot exceed 200 characters'
            ),
        },
        {
          name: 'airportCode',
          label: 'Airport Code',
          type: 'text',
          required: false,
          placeholder: 'Enter 3-letter IATA code (e.g., DEL)',
          validation: z
            .string()
            .optional()
            .refine(
              val => !val || /^[A-Z]{3}$/.test(val),
              'Airport code must be exactly 3 uppercase letters'
            ),
        },
      ],
    },
    {
      title: 'Additional Information',
      description: 'Optional tracking and identification details',
      fields: [
        {
          name: 'hubTrackingName',
          label: 'Hub Tracking Name',
          type: 'text',
          required: false,
          placeholder: 'Enter internal tracking name/alias',
          validation: z
            .string()
            .optional()
            .refine(
              val => !val || /^[A-Z0-9_-]*$/.test(val),
              'Hub tracking name can only contain uppercase letters, numbers, underscores and hyphens'
            )
            .refine(
              val => !val || val.length <= 100,
              'Hub tracking name cannot exceed 100 characters'
            ),
        },
      ],
    },
  ];

  const handleAdd = () => {
    setEditingHub(null);
    setIsFormOpen(true);
  };

  const handleEdit = (hub: Hub) => {
    setEditingHub(hub);
    setIsFormOpen(true);
  };

  const handleDelete = (hub: Hub) => {
    if (window.confirm(`Are you sure you want to delete hub "${hub.name}"?`)) {
      deleteHubMutation.mutate(hub._id);
    }
  };

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ['hubs'] });
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      Object.entries(currentFilters).forEach(([key, value]) => {
        if (value && value !== 'all') {
          params.append(key, value);
        }
      });

      const response = await axios.get(
        `/api/master-data/hub/export?${params.toString()}`,
        {
          responseType: 'blob',
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `hubs-${new Date().toISOString().split('T')[0]}.csv`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast({
        title: 'Export successful',
        description: 'Hub data has been exported successfully.',
      });
    } catch (error) {
      toast({
        title: 'Export failed',
        description: 'Failed to export hub data. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleFormSubmit = (data: any) => {
    console.log('Form data before cleaning:', data);
    console.log('Country field in form data:', data.country);

    // Don't clean up the data - send everything as is to preserve all fields
    const cleanedData = { ...data };

    // Ensure all optional fields are included, even if empty
    const optionalFields = [
      'contactNo',
      'email',
      'address',
      'airportName',
      'airportCode',
      'country',
      'stateCode',
      'hubTrackingName',
    ];
    optionalFields.forEach(field => {
      if (
        !(field in cleanedData) ||
        cleanedData[field] === null ||
        cleanedData[field] === undefined
      ) {
        cleanedData[field] = '';
      }
    });

    console.log('Form data after cleaning:', cleanedData);
    console.log('Country field after cleaning:', cleanedData.country);

    if (editingHub) {
      updateHubMutation.mutate({ ...cleanedData, _id: editingHub._id });
    } else {
      createHubMutation.mutate(cleanedData);
    }
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setEditingHub(null);
    setServerErrors({});
  };

  // Transform hub data for form
  const transformedEditingHub = editingHub
    ? {
        ...editingHub,
        company:
          typeof editingHub.company === 'object' && editingHub.company !== null
            ? editingHub.company._id
            : editingHub.company,
        country: editingHub.country || '', // Ensure country is set to empty string if not present
      }
    : { status: 'Active' }; // Default status for new hubs

  return (
    <div className='min-h-screen bg-background'>
      <div className='container mx-auto px-4 py-6 max-w-7xl'>
        <MasterDataTable
          title='Hub Management'
          description='Manage hub locations, operations, and configurations'
          data={hubs}
          columns={columns}
          isLoading={isLoading}
          onAdd={handleAdd}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onRefresh={handleRefresh}
          onExport={handleExport}
          filters={filters}
          searchPlaceholder='Search hubs by name, code, type, contact, email, address, airport details...'
          allowBulkOperations={false}
          allowImportExport={true}
          // Server-side filtering and pagination props
          serverSide={true}
          pagination={pagination}
          onPageChange={setCurrentPage}
          onPageSizeChange={setPageSize}
          onSearchChange={setSearchTerm}
          onFiltersChange={setCurrentFilters}
          onSortChange={setSortConfig}
          currentSearch={searchTerm}
          currentFilters={currentFilters}
          currentSort={sortConfig}
        />

        <MasterDataForm
          title={editingHub ? 'Edit Hub' : 'Add New Hub'}
          description={
            editingHub
              ? 'Update hub information and configuration settings.'
              : 'Create a new hub location with all necessary details and settings.'
          }
          sections={formSections}
          isOpen={isFormOpen}
          onClose={handleFormClose}
          onSubmit={handleFormSubmit}
          isLoading={createHubMutation.isPending || updateHubMutation.isPending}
          initialData={transformedEditingHub}
          mode={editingHub ? 'edit' : 'create'}
          serverErrors={serverErrors}
          entityName='Hub'
        />
      </div>
    </div>
  );
};

export default HubManagement;
